/**
 * A record, whole (WS-22): its number and statement; its source, date and
 * standing; who decided whether it's about the subject, and why — a person's
 * "not them" with its reason; the identifiers the identity check compared,
 * the subject's against the record's; the record's own words, never cut, the
 * identifiers it was matched on marked; the page "as research saw it"; and
 * the checklist items it bears on. What opens beside any view when a record
 * is named.
 */

import { Fragment, useState } from 'react';

import { Anchor, Badge, Group, Stack, Table, Text } from '@mantine/core';

import {
  identifiersOf,
  NOT_THEM_REASONS,
  type EvidenceItem,
  type IdentifierCompared,
} from '@/types/workbench';
import { CaptureViewer } from './CaptureViewer';
import { EVIDENCE_STATUS, sourceLabel, words } from './format';
import classes from './Workbench.module.css';

const IDENTITY: Record<string, { label: string; color: string }> = {
  same: { label: 'About the subject', color: 'teal' },
  connected: { label: 'About a connection', color: 'grape' },
  different: { label: 'Not the subject', color: 'gray' },
  uncertain: { label: 'Not sure it is the subject', color: 'orange' },
};

/** A person's reason for "not them", in words. */
function reasonLabel(reason: unknown): string | null {
  return NOT_THEM_REASONS.find((r) => r.value === reason)?.label ?? null;
}

/** Who decided whether it's about the subject, if anyone has — and, for a
 * person's "not them", why. */
export function IdentityBadge({ basis }: Readonly<{ basis: Record<string, unknown> | null }>) {
  const verdict = typeof basis?.verdict === 'string' ? IDENTITY[basis.verdict] : undefined;
  if (!verdict) {
    return null;
  }
  const person = basis?.by === 'person';
  const tie = typeof basis?.connection === 'string' ? basis.connection : null;
  const why = person ? reasonLabel(basis?.reason) : null;
  return (
    <Badge
      size="xs"
      variant="light"
      color={verdict.color}
      style={{ textTransform: 'none' }}
      title={!person && typeof basis?.reason === 'string' ? basis.reason : undefined}
      data-testid="evidence-identity"
    >
      {verdict.label}
      {tie ? ` (${tie})` : ''}
      {why ? ` — ${why.toLowerCase()}` : ''} · {person ? 'a person' : 'the identity check'}
    </Badge>
  );
}

/** What a search covered, in words. */
export function scopeText(scope: Record<string, unknown> | null): string | null {
  if (!scope) {
    return null;
  }
  return Object.entries(scope)
    .map(
      ([key, value]) => `${words(key)}: ${Array.isArray(value) ? value.join(', ') : String(value)}`
    )
    .join(' · ');
}

/** The identifiers the identity check compared for a record: its own, or —
 * where a person has since decided — the check's, kept with theirs. */
export function comparedOn(record: EvidenceItem): IdentifierCompared[] {
  const basis = record.identityBasis;
  const own = identifiersOf(basis);
  if (own.length > 0) {
    return own;
  }
  const previously = basis?.previously;
  return previously && typeof previously === 'object'
    ? identifiersOf(previously as Record<string, unknown>)
    : [];
}

const AGREES: Record<IdentifierCompared['agrees'], { mark: string; color: string; title: string }> =
  {
    match: { mark: '✓', color: 'teal', title: 'matches' },
    conflict: { mark: '✗', color: 'red', title: 'conflicts' },
    silent: { mark: '–', color: 'gray', title: "the record doesn't say" },
  };

/** The identifiers compared: the subject's against the record's. */
export function IdentifiersTable({
  identifiers,
  subjectName,
}: Readonly<{ identifiers: IdentifierCompared[]; subjectName: string | null }>) {
  return (
    <Table
      withTableBorder
      verticalSpacing={2}
      horizontalSpacing="xs"
      fz="xs"
      data-testid="identifiers"
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th />
          <Table.Th>{subjectName ?? 'The subject'}</Table.Th>
          <Table.Th>This record</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {identifiers.map((compared) => {
          const agrees = AGREES[compared.agrees];
          return (
            <Table.Tr key={`${compared.identifier}-${compared.label}`}>
              <Table.Td c="dimmed">{compared.label}</Table.Td>
              <Table.Td>{compared.subject || '—'}</Table.Td>
              <Table.Td>{compared.record || '—'}</Table.Td>
              <Table.Td>
                <Text span size="xs" fw={700} c={agrees.color} title={agrees.title}>
                  {agrees.mark}
                </Text>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

/** The text with each marked passage highlighted. */
export function Marked({ text, marks }: Readonly<{ text: string; marks: string[] }>) {
  const lower = text.toLowerCase();
  const found = marks
    .filter((m) => m.trim().length > 1)
    .map((m) => ({ length: m.length, at: lower.indexOf(m.toLowerCase()) }))
    .filter((x) => x.at >= 0)
    .sort((a, b) => a.at - b.at);
  const parts: { text: string; mark: boolean }[] = [];
  let at = 0;
  for (const { length, at: start } of found) {
    if (start < at) {
      continue;
    }
    parts.push(
      { text: text.slice(at, start), mark: false },
      { text: text.slice(start, start + length), mark: true }
    );
    at = start + length;
  }
  parts.push({ text: text.slice(at), mark: false });
  return (
    <>
      {parts.map((p, i) =>
        p.mark ? (
          <mark key={i} className={classes.mark}>
            {p.text}
          </mark>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        )
      )}
    </>
  );
}

function dateOf(record: EvidenceItem): string {
  return new Date(record.retrievedAt ?? record.createdAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface RecordViewProps {
  record: EvidenceItem;
  reportId: string;
  /** The subject it's about, as the case names them. */
  subjectName: string | null;
  /** The checklist items it bears on, by id. */
  checklistTitles?: Map<string, string>;
  onOpenChecklistItem?: (itemId: string) => void;
}

export function RecordView({
  record,
  reportId,
  subjectName,
  checklistTitles,
  onOpenChecklistItem,
}: Readonly<RecordViewProps>) {
  const [seeing, setSeeing] = useState(false);
  const status = EVIDENCE_STATUS[record.status];
  const identifiers = comparedOn(record);
  const scope = record.kind === 'search' ? scopeText(record.scope) : null;
  const note =
    record.identityBasis?.by === 'person' && typeof record.identityBasis.note === 'string'
      ? record.identityBasis.note
      : null;
  return (
    <Stack gap="xs" data-testid={`record-${record.id}`}>
      <Group gap={6} wrap="wrap">
        <Text fw={700} size="sm">
          Record {record.number}
        </Text>
        <Badge size="xs" variant="light" color={status.color}>
          {status.label}
        </Badge>
        {record.supersededAt && (
          <Badge size="xs" variant="outline" color="gray">
            Superseded
          </Badge>
        )}
        {record.kind === 'search' && (
          <Badge size="xs" variant="outline" color="gray">
            Search
          </Badge>
        )}
      </Group>
      <Text size="sm" style={{ wordBreak: 'break-word' }} data-testid="record-statement">
        {record.statement}
      </Text>
      <Text size="xs" c="dimmed">
        {sourceLabel(record.sourceType)} · {words(record.basis)} · {dateOf(record)}
        {subjectName ? ` · about ${subjectName}` : ''}
      </Text>
      <Group gap="md" wrap="wrap">
        {record.url && (
          <Anchor href={record.url} target="_blank" rel="noopener noreferrer" size="xs">
            {record.url}
          </Anchor>
        )}
        {record.hasCapture && (
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => setSeeing(true)}
            data-testid={`record-capture-${record.id}`}
          >
            As research saw it
          </Anchor>
        )}
      </Group>
      <Group gap={6}>
        <IdentityBadge basis={record.identityBasis} />
      </Group>
      {note && (
        <Text size="xs" c="dimmed">
          Their note: {note}
        </Text>
      )}
      {identifiers.length > 0 && (
        <IdentifiersTable identifiers={identifiers} subjectName={subjectName} />
      )}
      {record.statusReason && (
        <Text size="xs" c="dimmed">
          {record.statusReason}
        </Text>
      )}
      {scope && (
        <Text size="xs" c="dimmed">
          Searched — {scope}
        </Text>
      )}
      {record.quote && (
        <div className={classes.recordText} data-testid="record-text">
          <Marked
            text={record.quote}
            marks={identifiers.filter((i) => i.agrees !== 'silent').map((i) => i.record)}
          />
        </div>
      )}
      {record.links.length > 0 && (
        <Stack gap={2}>
          {record.links.map((link) => {
            const title = checklistTitles?.get(link.checklistItemId) ?? 'a checklist item';
            const text = `${link.relation === 'supports' ? 'Supports' : link.relation === 'contradicts' ? 'Contradicts' : 'Context for'} ${title}`;
            return onOpenChecklistItem ? (
              <Anchor
                key={`${link.checklistItemId}-${link.relation}`}
                component="button"
                type="button"
                size="xs"
                style={{ textAlign: 'left' }}
                onClick={() => onOpenChecklistItem(link.checklistItemId)}
                data-testid={`record-checklist-${link.checklistItemId}`}
              >
                {text}
              </Anchor>
            ) : (
              <Text key={`${link.checklistItemId}-${link.relation}`} size="xs">
                {text}
              </Text>
            );
          })}
        </Stack>
      )}
      {record.hasCapture && (
        <CaptureViewer
          reportId={reportId}
          evidenceId={record.id}
          opened={seeing}
          onClose={() => setSeeing(false)}
        />
      )}
    </Stack>
  );
}
